import { type DraggableAttributes } from '@dnd-kit/core';
import {
    centsToFormattedDollars,
    convertDecimalToPercentageForDisplay,
} from '@fruitful/fruitils';
import {
    type DeepPartial,
    type MoneyMapAccountWithAccountDetails,
    type MoneyMapCategory,
    MoneyMapCategory as MoneyMapCategoryEnum,
    PaycheckRoutingRuleType,
} from '@fruitful/types';
import React from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    GripVerticalIcon,
    Icon,
    InfoButtonWithTooltip,
    isEmpty,
    MonoLabelRegular,
    redactPrivate,
    SquarePenIcon,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { MoneyMapAccountIcon } from './MoneyMapAccountIcon';

export interface RoutingRuleCardProps {
    ruleId?: string;
    accountName: string;
    accountSubtitle?: string;
    description?: string | null;
    category: MoneyMapCategory;
    account: DeepPartial<MoneyMapAccountWithAccountDetails>;
    routingRuleType?: PaycheckRoutingRuleType | null;
    percentage?: number | null;
    fixedAmountCents?: number | null;
    targetBalanceCents?: number | null;
    monthlyContributionMaxCents?: number | null;
    yearlyContributionMaxCents?: number | null;
    hasRetirementContributionLimit?: boolean;
    isBackdoorTrad?: boolean;
    percentageWarning?: boolean;
    onPressEdit?: () => void;
    editable?: boolean;
    drag?: () => void;
    onDragHandlePressIn?: () => void;
    onDragHandlePressOut?: () => void;
    setActivatorNodeRef?: (element: HTMLElement | null) => void;
    dragListeners?: Record<string, unknown>;
    dragAttributes?: DraggableAttributes;
}

const RuleDataTag = ({
    text,
    color,
    isPrivate = false,
}: {
    text: string;
    color: string;
    isPrivate?: boolean;
}) => {
    return (
        <View
            alignSelf="flex-start"
            backgroundColor="$gray200"
            borderRadius={16}
            paddingVertical={4}
            paddingHorizontal={6}
            {...(isPrivate && redactPrivate())}
        >
            <MonoLabelRegular color={color}>{text}</MonoLabelRegular>
        </View>
    );
};

const RuleDataRow = ({ children }: { children: React.ReactNode }) => {
    return (
        <XStack gap={8} alignItems="center" flexWrap="wrap">
            {children}
        </XStack>
    );
};

const getContributeDisplay = ({
    routingRuleType,
    percentage,
    fixedAmountCents,
}: Pick<
    RoutingRuleCardProps,
    'routingRuleType' | 'percentage' | 'fixedAmountCents'
>): string => {
    if (
        routingRuleType === PaycheckRoutingRuleType.FIXED &&
        fixedAmountCents != null
    ) {
        return centsToFormattedDollars(fixedAmountCents, false);
    }

    if (percentage != null) {
        if (percentage === 1) return '100%';
        return convertDecimalToPercentageForDisplay(percentage);
    }

    return '100%';
};

const LegacyRuleData = ({
    routingRuleType,
    targetBalanceCents,
    monthlyContributionMaxCents,
    yearlyContributionMaxCents,
    hasRetirementContributionLimit,
    percentage,
    fixedAmountCents,
}: Pick<
    RoutingRuleCardProps,
    | 'routingRuleType'
    | 'targetBalanceCents'
    | 'monthlyContributionMaxCents'
    | 'yearlyContributionMaxCents'
    | 'hasRetirementContributionLimit'
    | 'percentage'
    | 'fixedAmountCents'
>) => {
    // Treat leftover targetBalanceCents as min-balance. Fine for real GOAL
    // targets; misleading for BILLS, whose monthly figure is stored there.
    const isMinimumBalanceRule =
        routingRuleType === PaycheckRoutingRuleType.MINIMUM_BALANCE ||
        (targetBalanceCents ?? 0) > 0;
    const hasMonthlyMax = (monthlyContributionMaxCents ?? 0) > 0;
    const hasYearlyMax = (yearlyContributionMaxCents ?? 0) > 0;
    const hasRetirementLimit = hasRetirementContributionLimit === true;

    const contributeDisplay = getContributeDisplay({
        routingRuleType,
        percentage,
        fixedAmountCents,
    });

    const showConditions =
        isMinimumBalanceRule ||
        hasMonthlyMax ||
        hasYearlyMax ||
        hasRetirementLimit;

    return (
        <YStack padding={12} borderRadius={8} backgroundColor="$white" gap={8}>
            <RuleDataRow>
                <RuleDataTag text="CONTRIBUTE" color="$gray1200" />
                <RuleDataTag
                    text={contributeDisplay}
                    color="$green900"
                    isPrivate
                />
            </RuleDataRow>
            {showConditions && (
                <YStack>
                    {isMinimumBalanceRule && (
                        <RuleDataRow>
                            <RuleDataTag text="UNTIL" color="$gray1200" />
                            <MonoLabelRegular color="$gray1200">
                                available balance
                            </MonoLabelRegular>
                            <MonoLabelRegular color="$gray1200">
                                =
                            </MonoLabelRegular>
                            <RuleDataTag
                                text={centsToFormattedDollars(
                                    targetBalanceCents ?? 0,
                                    false,
                                )}
                                color="$green900"
                                isPrivate
                            />
                        </RuleDataRow>
                    )}
                    {hasMonthlyMax && (
                        <>
                            {isMinimumBalanceRule && (
                                <MonoLabelRegular
                                    paddingVertical={8}
                                    color="$gray1200"
                                >
                                    or
                                </MonoLabelRegular>
                            )}
                            <RuleDataRow>
                                <RuleDataTag text="UNTIL" color="$gray1200" />
                                <MonoLabelRegular color="$gray1200">
                                    monthly contribution
                                </MonoLabelRegular>
                                <MonoLabelRegular color="$gray1200">
                                    =
                                </MonoLabelRegular>
                                <RuleDataTag
                                    text={centsToFormattedDollars(
                                        monthlyContributionMaxCents ?? 0,
                                        false,
                                    )}
                                    color="$green900"
                                    isPrivate
                                />
                            </RuleDataRow>
                        </>
                    )}
                    {hasYearlyMax && (
                        <>
                            {(isMinimumBalanceRule || hasMonthlyMax) && (
                                <MonoLabelRegular
                                    paddingVertical={8}
                                    color="$gray1200"
                                >
                                    or
                                </MonoLabelRegular>
                            )}
                            <RuleDataRow>
                                <RuleDataTag text="UNTIL" color="$gray1200" />
                                <MonoLabelRegular color="$gray1200">
                                    annual contribution
                                </MonoLabelRegular>
                                <MonoLabelRegular color="$gray1200">
                                    =
                                </MonoLabelRegular>
                                <RuleDataTag
                                    text={centsToFormattedDollars(
                                        yearlyContributionMaxCents ?? 0,
                                        false,
                                    )}
                                    color="$green900"
                                    isPrivate
                                />
                            </RuleDataRow>
                        </>
                    )}
                    {hasRetirementLimit && (
                        <>
                            {(isMinimumBalanceRule ||
                                hasMonthlyMax ||
                                hasYearlyMax) && (
                                <MonoLabelRegular
                                    paddingVertical={8}
                                    color="$gray1200"
                                >
                                    or
                                </MonoLabelRegular>
                            )}
                            <RuleDataRow>
                                <RuleDataTag text="UNTIL" color="$gray1200" />
                                <MonoLabelRegular color="$gray1200">
                                    annual IRA contribution limit
                                </MonoLabelRegular>
                            </RuleDataRow>
                        </>
                    )}
                </YStack>
            )}
        </YStack>
    );
};

const EditableRuleCard = (props: RoutingRuleCardProps) => {
    const {
        accountName,
        category,
        account,
        onPressEdit,
        percentageWarning,
        setActivatorNodeRef,
        dragListeners,
        dragAttributes,
    } = props;

    const { role: _omitRole, ...safeAttributes } = (dragAttributes ?? {}) as {
        role?: string;
    } & Record<string, unknown>;

    const contributeDisplay = getContributeDisplay({
        routingRuleType: props.routingRuleType,
        percentage: props.percentage,
        fixedAmountCents: props.fixedAmountCents,
    });

    const valueColor =
        percentageWarning === true &&
        props.routingRuleType !== PaycheckRoutingRuleType.FIXED
            ? '$orange1000'
            : '$green900';

    const isMinimumBalanceRule =
        props.routingRuleType === PaycheckRoutingRuleType.MINIMUM_BALANCE;
    const hasTargetBalance =
        props.targetBalanceCents != null ? props.targetBalanceCents > 0 : false;
    const hasMonthlyMax = (props.monthlyContributionMaxCents ?? 0) > 0;
    const hasYearlyMax = (props.yearlyContributionMaxCents ?? 0) > 0;
    const hasRetirementLimit = props.hasRetirementContributionLimit === true;
    // Bills and spend hold a monthly figure whichever field stores it: bills are
    // persisted as MINIMUM_BALANCE/targetBalanceCents and spend as
    // PERCENTAGE/monthlyContributionMaxCents, but income split caps both per month.
    const isMonthlyExpenseRule =
        category === MoneyMapCategoryEnum.BILLS ||
        category === MoneyMapCategoryEnum.SPEND;

    const conditionRows: Array<{ label: string; value: string }> = [];
    if (isMonthlyExpenseRule) {
        const monthlyExpenseCents = isMinimumBalanceRule
            ? props.targetBalanceCents
            : props.monthlyContributionMaxCents;

        if ((monthlyExpenseCents ?? 0) > 0) {
            conditionRows.push({
                label: 'until monthly contribution =',
                value: centsToFormattedDollars(monthlyExpenseCents ?? 0, false),
            });
        }
    } else {
        if (isMinimumBalanceRule && hasTargetBalance) {
            conditionRows.push({
                label: 'until available balance =',
                value: centsToFormattedDollars(
                    props.targetBalanceCents ?? 0,
                    false,
                ),
            });
        }
        if (hasMonthlyMax) {
            conditionRows.push({
                label: 'until monthly contribution =',
                value: centsToFormattedDollars(
                    props.monthlyContributionMaxCents ?? 0,
                    false,
                ),
            });
        }
    }
    if (hasYearlyMax) {
        conditionRows.push({
            label: 'until annual contribution =',
            value: centsToFormattedDollars(
                props.yearlyContributionMaxCents ?? 0,
                false,
            ),
        });
    }
    if (hasRetirementLimit) {
        conditionRows.push({
            label: 'until annual IRA limit',
            value: '',
        });
    }

    const isGoal = category === MoneyMapCategoryEnum.GOALS;
    const showDragHandle =
        isGoal && (setActivatorNodeRef != null || props.drag != null);
    const hasDescription = isGoal && !isEmpty(props.description);
    const isBackdoorTrad = props.isBackdoorTrad === true;

    const editActions = (
        <XStack alignItems="center">
            <View
                alignItems="center"
                justifyContent="center"
                minHeight={32}
                minWidth={32}
                animation="button"
                pressStyle={{ scale: 0.95 }}
                onPress={onPressEdit}
                cursor="pointer"
            >
                <Icon icon={SquarePenIcon} size={16} color="$petalDarkest" />
            </View>
            {showDragHandle && (
                <View
                    ref={setActivatorNodeRef}
                    {...(dragListeners ?? {})}
                    {...safeAttributes}
                    alignItems="center"
                    justifyContent="center"
                    minHeight={32}
                    minWidth={32}
                    cursor="grab"
                    onPressIn={props.onDragHandlePressIn}
                    onLongPress={props.drag}
                    onPressOut={props.onDragHandlePressOut}
                >
                    <Icon
                        icon={GripVerticalIcon}
                        size={16}
                        color="$petalDarkest"
                    />
                </View>
            )}
        </XStack>
    );

    return (
        <YStack
            backgroundColor="$white"
            borderRadius={16}
            padding={14}
            gap={12}
            minHeight={72}
            overflow="hidden"
        >
            {hasDescription ? (
                <XStack
                    backgroundColor="$petalLight"
                    paddingHorizontal={16}
                    paddingVertical={12}
                    marginHorizontal={-14}
                    marginTop={-14}
                    alignItems="center"
                    justifyContent="space-between"
                    gap={12}
                >
                    <BodySmallRegular color="$petalDarkest" flex={1}>
                        {props.description}
                    </BodySmallRegular>
                    {editActions}
                </XStack>
            ) : null}
            <XStack alignItems="center" justifyContent="space-between">
                <XStack gap={8} alignItems="center">
                    <BodyMediumRegular>Contribute</BodyMediumRegular>
                    <BodyMediumRegular color={valueColor} {...redactPrivate()}>
                        {contributeDisplay}
                    </BodyMediumRegular>
                </XStack>
                {hasDescription ? null : editActions}
            </XStack>

            <XStack gap={8} alignItems="center">
                <BodyMediumRegular>to</BodyMediumRegular>
                <MoneyMapAccountIcon category={category} account={account} />
                <XStack alignItems="center">
                    <BodyMediumRegular color="$green900">
                        {accountName}
                    </BodyMediumRegular>
                    {isBackdoorTrad && (
                        <InfoButtonWithTooltip tooltipContent="Contributions to this IRA will be promptly converted to the linked Roth IRA as part of a backdoor Roth conversion. Funds will not be invested while held in this account. Reach out to your Guide if you have questions." />
                    )}
                </XStack>
            </XStack>

            {conditionRows.map((row, index) => (
                <XStack key={row.label} gap={8} alignItems="center">
                    <BodyMediumRegular>
                        {index > 0 ? `or ${row.label}` : row.label}
                    </BodyMediumRegular>
                    {row.value.length > 0 && (
                        <BodyMediumRegular
                            color="$green900"
                            {...redactPrivate()}
                        >
                            {row.value}
                        </BodyMediumRegular>
                    )}
                </XStack>
            ))}

            {setActivatorNodeRef != null && !showDragHandle && (
                <View
                    ref={setActivatorNodeRef}
                    {...(dragListeners ?? {})}
                    {...safeAttributes}
                    display="none"
                />
            )}
        </YStack>
    );
};

export const MoneyMapRoutingRuleCard = (props: RoutingRuleCardProps) => {
    const {
        accountName,
        accountSubtitle,
        category,
        account,
        editable = false,
    } = props;

    if (editable) {
        return <EditableRuleCard {...props} />;
    }

    return (
        <XStack
            paddingTop={16}
            paddingBottom={16}
            paddingHorizontal={16}
            borderRadius={16}
            backgroundColor="$gray200"
            alignItems="center"
        >
            <YStack flexGrow={1} flexShrink={1} gap={16}>
                <XStack alignItems="center" justifyContent="space-between">
                    <XStack
                        gap={12}
                        alignItems="center"
                        flex={1}
                        flexShrink={1}
                    >
                        <MoneyMapAccountIcon
                            category={category}
                            account={account}
                        />
                        <YStack gap={4} flexShrink={1}>
                            <BodyMediumRegular>{accountName}</BodyMediumRegular>
                            {accountSubtitle != null && (
                                <BodySmallRegular color="$gray1000">
                                    {accountSubtitle}
                                </BodySmallRegular>
                            )}
                        </YStack>
                    </XStack>
                </XStack>
                <LegacyRuleData
                    routingRuleType={props.routingRuleType}
                    targetBalanceCents={props.targetBalanceCents}
                    monthlyContributionMaxCents={
                        props.monthlyContributionMaxCents
                    }
                    yearlyContributionMaxCents={
                        props.yearlyContributionMaxCents
                    }
                    hasRetirementContributionLimit={
                        props.hasRetirementContributionLimit
                    }
                    percentage={props.percentage}
                    fixedAmountCents={props.fixedAmountCents}
                />
            </YStack>
        </XStack>
    );
};
